/**
 * phase-fix-r0-region-alias-submission-types (R0 of the activity-model plan) —
 * canonical region vocabulary + normaliser.
 *
 * V2 carries two spellings for the same regions:
 *
 *   - The **canonical codes** below are what the reference tables store
 *     (`SubmissionTypesCatalog.Region`, seeded by
 *     `migrations/20260901020000_seed_submission_types_catalog.ts`).
 *   - The **display names** are what the web client sends and what
 *     `Submissions.Region` holds (`web/src/constants/regions.ts`).
 *
 * Six of the seven regions spell both the same. Health Canada does not:
 * the client says `'Health Canada'`, the catalog says `'HC'`. That single
 * mismatch silently emptied the New Submission wizard's application-type
 * dropdown for Health Canada — see the activity-model design report §2.6.
 *
 * This module is the single source of truth for that normalisation, so the
 * later phases of the plan (R1's catalog canonicalisation in particular)
 * have one place to extend rather than a fresh ad-hoc map per call site.
 *
 * Keep the alias map evidence-driven: only add an alias for a spelling
 * something actually sends. Speculative aliases hide real drift.
 */

/** Canonical region codes — the vocabulary the reference tables store. */
export const REGION_CODES = ['FDA', 'HC', 'EMA', 'MHRA', 'PMDA', 'TGA', 'Swissmedic'] as const;

export type RegionCode = (typeof REGION_CODES)[number];

/**
 * Display-name → canonical-code aliases, keyed lowercase.
 *
 * Only Health Canada needs one today: every other entry in the web client's
 * `REGIONS` constant is already spelled as its canonical code.
 */
const REGION_ALIASES: Readonly<Record<string, RegionCode>> = Object.freeze({
  'health canada': 'HC',
  // phase-forms-workflow-follow-up (Item B) — the DocumentTemplates KB seed
  // (20260525015138_seed_document_templates_from_kb.ts) stores HC templates'
  // Region as the no-space 'HealthCanada'. Template auto-link canonicalises a
  // template's Region via regionCode(), so this spelling must resolve too.
  healthcanada: 'HC',
});

/** Canonical codes keyed lowercase, so identity lookups are case-insensitive. */
const CODES_BY_LOWER: ReadonlyMap<string, RegionCode> = new Map(
  REGION_CODES.map((c) => [c.toLowerCase(), c] as const),
);

/**
 * Normalise a region string to its canonical code.
 *
 * Accepts a canonical code (any case) or a known display-name alias.
 * Returns `null` for anything unrecognised — callers decide whether that is
 * an error or a warning; this function never guesses.
 */
export function regionCode(input: string | null | undefined): RegionCode | null {
  if (typeof input !== 'string') return null;
  const key = input.trim().toLowerCase();
  if (key === '') return null;
  return CODES_BY_LOWER.get(key) ?? REGION_ALIASES[key] ?? null;
}

/** Type guard for an already-canonical region code. */
export function isRegionCode(value: unknown): value is RegionCode {
  return typeof value === 'string' && CODES_BY_LOWER.has(value.toLowerCase());
}

/**
 * T15-D8 — THE canonical region list. One row per region: the canonical
 * code (the spelling SpecFiles and every reference table store), the value a
 * submission stores in `Submissions.Region`, and the name shown to people.
 * Served by `GET /api/v1/regions`; the web client's `REGIONS` constant is
 * pinned to `submissionValue` in this order by
 * tests/lib/regions-canonical-list.test.ts. Existing submissions are NOT
 * backfilled — `submissionValue` stays the stored spelling, and every
 * comparison goes through `regionCode()`.
 */
export interface RegionDefinition {
  readonly code: RegionCode;
  readonly submissionValue: string;
  readonly displayName: string;
}

export const REGION_DEFINITIONS: ReadonlyArray<RegionDefinition> = Object.freeze([
  { code: 'FDA', submissionValue: 'FDA', displayName: 'FDA' },
  { code: 'EMA', submissionValue: 'EMA', displayName: 'EMA' },
  { code: 'MHRA', submissionValue: 'MHRA', displayName: 'MHRA' },
  { code: 'HC', submissionValue: 'Health Canada', displayName: 'Health Canada' },
  { code: 'PMDA', submissionValue: 'PMDA', displayName: 'PMDA' },
  { code: 'TGA', submissionValue: 'TGA', displayName: 'TGA' },
  { code: 'Swissmedic', submissionValue: 'Swissmedic', displayName: 'Swissmedic' },
] as const);

/** The canonical definition for any accepted spelling, or null. */
export function regionDefinition(input: string | null | undefined): RegionDefinition | null {
  const code = regionCode(input);
  return code === null ? null : (REGION_DEFINITIONS.find((d) => d.code === code) ?? null);
}
